import React, { ComponentProps } from 'react';
import { Button, Form } from 'antd';

import EventsSelect from '@src/emp-3.0/components/EventsSelect';
import { EEventType, EMetricTypes } from '@src/emp-3.0/types';
import useFieldRequiredStatus from '@src/pages/workflow/hooks/useFieldRequiredStatus';
import CustomFormItem from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import { IMetricEventsProps } from '../typed';
import EventItem from '@src/emp-3.0/components/Metrics/Flow/FlowForm/EventItem';
import { EventLabel } from '@src/emp-3.0/components/Metrics/components';
import { useMetricBuilderStore } from '../store';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
const MetricEvents: React.FC<IMetricEventsProps> = ({ form, metricType, isView }) => {
  const { initialEvent, setInitialEvent, setCompleteEvent, completeEvent } =
    useMetricBuilderStore();
  const { setActiveTool } = useFlowStore();
  const initialEventId = Form.useWatch(['definition', 'first_event'], form);
  const completeEventId = Form.useWatch(['definition', 'second_event'], form);
  const eventId = Form.useWatch(['definition', 'event'], form);

  const { status: firstEventStatus, enableRequired } = useFieldRequiredStatus([
    'definition',
    'first_event',
  ]);
  const { status: eventStatus, enableRequired: enableRequiredForEvent } = useFieldRequiredStatus([
    'definition',
    'event',
  ]);
  const { status: secondEventStatus } = useFieldRequiredStatus(['definition', 'second_event']);

  const isSingleEvent = metricType === EMetricTypes.SINGLE_EVENT;

  const onEventChange = (_, type, event) => {
    const eventType = typeof event === 'string' ? 'raw' : 'mapped';
    if (type === EEventType.FIRST_EVENT) {
      setInitialEvent(null);
    }
    if (type === EEventType.EVENT) {
      setInitialEvent(null);
    }
    if (type === EEventType.SECOND_EVENT) {
      setCompleteEvent(null);
    }
    form.setFieldsValue({
      definition: { [type]: eventType === 'mapped' ? event.event_id : event },
    });
  };

  return (
    <>
      <div id={`${EEventType.FIRST_EVENT}`}>
        <CustomFormItem
          name={isSingleEvent ? ['definition', 'event'] : ['definition', 'first_event']}
          dependencies={[['definition', 'second_event']]}
          label={
            <EventLabel
              prefix={null}
              tooltip="The initial event defines the flow start, such that clicking the login or checkout button"
              text={isSingleEvent ? 'Event' : 'Initial Event'}
            />
          }
          labelAlign="left"
          labelCol={{ span: 6 }}
          validateStatus={firstEventStatus || eventStatus}
          rules={
            [
              enableRequired && { required: true, message: 'Initial Event is required' },
              enableRequiredForEvent && {
                required: true,
                message: 'Event is required',
                whitespace: true,
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const secondEvent = getFieldValue(['definition', 'second_event']);
                  if (!value || secondEvent !== value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Initial Event cannot be the same as Complete Event or Error Event'),
                  );
                },
              }),
            ].filter(Boolean) as ComponentProps<typeof Form.Item>['rules']
          }
        >
          {isView ? (
            <div />
          ) : (
            <EventsSelect
              eventType={isSingleEvent ? EEventType.EVENT : EEventType.FIRST_EVENT}
              onChange={(value, event) => {
                if (value) {
                  onEventChange(
                    value,
                    isSingleEvent ? EEventType.EVENT : EEventType.FIRST_EVENT,
                    event,
                  );
                }
              }}
            ></EventsSelect>
          )}
        </CustomFormItem>

        <EventItem
          style={{ marginLeft: '28px' }}
          type={isSingleEvent ? EEventType.EVENT : EEventType.FIRST_EVENT}
          eventId={isSingleEvent ? eventId : initialEventId}
          event={initialEvent}
          viewModeProps={{
            onEdit: (event) => {
              setInitialEvent(event);
            },
            onDelete: () => {
              setInitialEvent(null);
              if (isSingleEvent) {
                form.setFieldsValue({
                  definition: { event: null },
                });
              } else {
                form.setFieldsValue({
                  definition: { first_event: null },
                });
                form.validateFields([['definition', 'second_event']]);
              }
            },
            onClone: (event) => {
              setInitialEvent(event);
            },
          }}
          editModeProps={{
            onSave: (event) => {
              if (isSingleEvent) {
                form.setFieldsValue({
                  definition: { event: event.event_id },
                });
              } else {
                form.setFieldsValue({
                  definition: { first_event: event.event_id },
                });
              }
              setTimeout(() => {
                setInitialEvent(null);
              }, 100);
            },
            onCancel: () => {
              setInitialEvent(null);
            },
            onChange: (event) => {
              if (Number(event)) {
                if (isSingleEvent) {
                  form.setFieldsValue({
                    definition: { event },
                  });
                } else {
                  form.setFieldsValue({
                    definition: { first_event: event },
                  });
                  form.validateFields([['definition', 'second_event']]);
                }
                setTimeout(() => {
                  setInitialEvent(null);
                }, 100);
              } else {
                setInitialEvent(event);
              }
            },
          }}
        />
      </div>

      {metricType !== EMetricTypes.SINGLE_EVENT && (
        <>
          <div style={{ height: '16px' }}></div>
          <div id={`${EEventType.SECOND_EVENT}`}>
            <CustomFormItem
              name={['definition', 'second_event']}
              dependencies={[['definition', 'first_event']]}
              label={
                <EventLabel
                  prefix={null}
                  tooltip="The complete event defines the flow end, such that displaying the homepage or confirming payment complete"
                  text="Complete Event"
                />
              }
              labelAlign="left"
              labelCol={{ span: 6 }}
              validateStatus={secondEventStatus}
              rules={
                [
                  enableRequired && { required: true, message: 'Complete Event is required' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const firstEvent = getFieldValue(['definition', 'first_event']);
                      if (!value || firstEvent !== value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          'Complete Event cannot be the same as Initial Event or Error Event',
                        ),
                      );
                    },
                  }),
                ].filter(Boolean) as ComponentProps<typeof Form.Item>['rules']
              }
            >
              {isView ? (
                <div />
              ) : (
                <EventsSelect
                  eventType={EEventType.SECOND_EVENT}
                  onChange={(value, event) => {
                    onEventChange(value, EEventType.SECOND_EVENT, event);
                  }}
                ></EventsSelect>
              )}
            </CustomFormItem>

            <EventItem
              style={{ marginLeft: '28px' }}
              type={EEventType.SECOND_EVENT}
              event={completeEvent}
              eventId={completeEventId}
              viewModeProps={{
                onEdit: (event) => {
                  setCompleteEvent(event);
                },
                onDelete: () => {
                  setCompleteEvent(null);
                  form.setFieldsValue({
                    definition: { second_event: null },
                  });
                  form.validateFields([['definition', 'first_event']]);
                },
                onClone: (event) => {
                  setCompleteEvent(event);
                },
              }}
              editModeProps={{
                onSave: (event) => {
                  form.setFieldsValue({
                    definition: { second_event: event.event_id },
                  });
                  setTimeout(() => {
                    setCompleteEvent(null);
                  }, 100);
                },
                onCancel: () => {
                  setCompleteEvent(null);
                },
                onChange: (event) => {
                  if (Number(event)) {
                    form.setFieldsValue({
                      definition: { second_event: event },
                    });
                    form.validateFields([['definition', 'first_event']]);
                    setTimeout(() => {
                      setCompleteEvent(null);
                    }, 100);
                  } else {
                    setCompleteEvent(event);
                  }
                },
              }}
            />
          </div>
        </>
      )}
      {isView ? null : (
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type={'link'}
            onClick={() => {
              setActiveTool('validation');
            }}
            disabled={isSingleEvent ? !eventId : !initialEventId && !completeEventId}
          >
            Validate Event Coverage
          </Button>
        </div>
      )}
    </>
  );
};

export default MetricEvents;
